import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { Truck, MapPin, Ship, TrainFront, X } from "lucide-react";
import { FaTruckFront } from "react-icons/fa6";
import Cookies from "js-cookie";
import httpClient from "../../../../../../api/httpClient";
import { toast } from "react-toastify";
import FullScreenSlideUpModal from "../../../../../../components/ACS-Dashboard/ACS-Blocks/FullScreenSlideUpModal";
import notify from "../../../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import "react-toastify/dist/ReactToastify.css";
import Table1 from "../../../../../../components/ACS-Dashboard/ACS-Blocks/TableSecondary";
import { resourceSelectionApi } from "../../../../Resource-Selection/api/resourceSelectionApi";
import {
  buildRankedCarrierMap,
  ensureArray,
  normalizeRateShoppingResults,
  parseRateShoppingResponse,
} from "../rateShoppingUtils";
import {
  collectCarrierScoringBreakdown,
  extractSelectionOutcomeId,
  getCachedScoringExplanation,
  invalidateScoringExplanationCache,
  isUsefulScoringExplanation,
  pickLatestSelectionOutcomeId,
  setCachedScoringExplanation,
} from "../../../../../../utils/scoringExplanationCache";

const officeCodeCookie = Cookies.get("officeCode");

const eq = (a, b) =>
  (a || "").toString().toUpperCase() === (b || "").toString().toUpperCase();

const compareCarriersByName = (a, b) =>
  (a.carrierName || a.carrierCode || "").localeCompare(
    b.carrierName || b.carrierCode || ""
  );

const formatScoreValue = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "number" && Number.isFinite(value)) {
    return Number.isInteger(value) ? String(value) : value.toFixed(4);
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const kpiFactorLabel = (row, index) =>
  row?.name ||
  row?.factor ||
  row?.kpi ||
  row?.kpi_name ||
  row?.kpiName ||
  row?.type ||
  row?.label ||
  `KPI ${index + 1}`;

const rowWeight = (row) =>
  row?.weight ??
  row?.percent ??
  row?.weight_pct ??
  row?.weightPct ??
  row?.kpi_weight ??
  row?.kpiWeight;

const rowScore = (row) =>
  row?.score ??
  row?.value ??
  row?.factor_value ??
  row?.factorValue ??
  row?.normalized_score ??
  row?.normalizedScore;

const rowValue = (row) => rowScore(row) ?? row?.cost ?? row?.price;

const rowContribution = (row) =>
  row?.contribution ??
  row?.weighted_score ??
  row?.weightedScore ??
  row?.kpi_contribution ??
  row?.kpiContribution;

const hasMetric = (getter, rows) =>
  rows.some((row) => {
    const value = getter(row);
    return value != null && value !== "";
  });

const isCostKpiRow = (row) => {
  const key = String(row?.name || row?.kpi || "")
    .toLowerCase()
    .replace(/\s+/g, "_");
  return key === "cost" || key === "price";
};

const renderScoreDetailsTable = (rows, { title, valueLabel = "Value" } = {}) => {
  if (!rows?.length) return null;

  const showWeight = hasMetric(rowWeight, rows);
  const showContribution = hasMetric(rowContribution, rows);

  return (
    <div>
      {title ? <p className="mb-2 font-semibold">{title}</p> : null}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50 text-dense-xs font-semibold uppercase tracking-wide text-gray-700">
            <tr>
              <th className="px-3 py-2">{valueLabel === "Score" ? "KPI" : "Factor"}</th>
              {showWeight ? <th className="px-3 py-2 text-right">Weight</th> : null}
              <th className="px-3 py-2 text-right">{valueLabel}</th>
              {showContribution ? (
                <th className="px-3 py-2 text-right">Contribution</th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={`${kpiFactorLabel(row, index)}-${index}`}
                className="border-t border-gray-100"
              >
                <td className="px-3 py-2">{kpiFactorLabel(row, index)}</td>
                {showWeight ? (
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatScoreValue(rowWeight(row))}
                  </td>
                ) : null}
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatScoreValue(valueLabel === "Score" ? rowScore(row) : rowValue(row))}
                </td>
                {showContribution ? (
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatScoreValue(rowContribution(row))}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const renderKpiMetricsTable = (rows) => {
  const displayRows = (rows || []).filter((row) => !isCostKpiRow(row));
  if (!displayRows.length) return null;

  const showKpiFactorFlag = displayRows.some((row) => row.usedInKpiFactor != null);
  const usesPenaltyColumn = displayRows.some((row) => row.penaltyMultiplier != null);

  return (
    <div>
      <p className="mb-1 font-semibold">Carrier KPIs</p>
      {usesPenaltyColumn ? (
        <p className="mb-2 rounded-md bg-gray-50 px-3 py-2 text-dense-xs text-gray-700">
          Penalty multiplier is derived from each KPI&apos;s performance rate (lower is better).
          KPIs with KPI factor Yes contribute weight × penalty multiplier to the overall KPI factor.
        </p>
      ) : null}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full table-fixed border-collapse text-sm">
          <thead className="bg-gray-50 text-dense-xs font-semibold text-gray-700">
            <tr>
              <th className="w-[36%] px-3 py-2 text-left">KPI</th>
              <th className="w-[22%] px-3 py-2 text-right">
                {usesPenaltyColumn ? "Penalty multiplier" : "Value"}
              </th>
              <th className="w-[22%] px-3 py-2 text-right">Performance rate</th>
              {showKpiFactorFlag ? (
                <th className="w-[20%] px-3 py-2 text-center">KPI factor</th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, index) => (
              <tr key={`${kpiFactorLabel(row, index)}-${index}`} className="border-t border-gray-100">
                <td className="px-3 py-2 text-left">{kpiFactorLabel(row, index)}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatScoreValue(
                    usesPenaltyColumn ? row.penaltyMultiplier : rowScore(row)
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatScoreValue(row.onTimeRate)}
                </td>
                {showKpiFactorFlag ? (
                  <td className="px-3 py-2 text-center">
                    {row.usedInKpiFactor ? "Yes" : row.usedInKpiFactor === false ? "No" : "—"}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const renderCalculationSection = (title, calc) => {
  const { formula, kpiFactor, steps = [] } = calc ?? {};
  if (!formula && !steps.length && kpiFactor == null) return null;

  const showNormalized = steps.some((row) => row.normalized != null && row.normalized !== "");
  const isKpiFactorSection = /kpi factor/i.test(title || "");
  const rawColumnLabel = isKpiFactorSection ? "Penalty multiplier" : "Raw";

  return (
    <div>
      <p className="mb-2 font-semibold">{title}</p>
      {formula ? (
        <p className="mb-2 rounded-md bg-gray-50 px-3 py-2 text-dense-xs text-gray-800">{formula}</p>
      ) : null}
      {isKpiFactorSection && kpiFactor != null ? (
        <p className="mb-2 text-dense-xs text-gray-700">
          KPI factor: {formatScoreValue(kpiFactor)}
        </p>
      ) : null}
      {steps.length ? (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 text-dense-xs font-semibold uppercase tracking-wide text-gray-700">
              <tr>
                <th className="px-3 py-2">Metric</th>
                <th className="px-3 py-2 text-right">Weight</th>
                <th className="px-3 py-2 text-right">{rawColumnLabel}</th>
                {showNormalized ? (
                  <th className="px-3 py-2 text-right">Normalized</th>
                ) : null}
                <th className="px-3 py-2 text-right">Contribution</th>
              </tr>
            </thead>
            <tbody>
              {steps.map((row, index) => (
                <tr key={`${kpiFactorLabel(row, index)}-${index}`} className="border-t border-gray-100">
                  <td className="px-3 py-2">
                    <div>{kpiFactorLabel(row, index)}</div>
                    {row.calculation ? (
                      <div className="text-dense-xs text-gray-600">{row.calculation}</div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatScoreValue(rowWeight(row))}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatScoreValue(rowScore(row))}
                  </td>
                  {showNormalized ? (
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatScoreValue(row.normalized)}
                    </td>
                  ) : null}
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatScoreValue(rowContribution(row))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

const renderPriceCalculationSection = (priceCalculation) => {
  if (!priceCalculation?.formula && priceCalculation?.price == null) return null;

  const rows = [
    { name: "Price", value: priceCalculation.cost },
    { name: "KPI factor", value: priceCalculation.kpiFactor },
    { name: "KPI-Cost", value: priceCalculation.price },
  ].filter((row) => row.value != null && row.value !== "");

  return (
    <div>
      <p className="mb-2 font-semibold">RankCost (KPI-Cost) calculation</p>
      {priceCalculation.formula ? (
        <p className="mb-2 rounded-md bg-gray-50 px-3 py-2 text-dense-xs text-gray-800">
          {priceCalculation.formula}
        </p>
      ) : null}
      {rows.length ? renderScoreDetailsTable(rows, { title: null, valueLabel: "Value" }) : null}
    </div>
  );
};

const renderScoreDetailsBreakdown = ({
  summaryRows = [],
  kpiScoreRows = [],
  kpiFactorCalculation = null,
  priceCalculation = null,
  rankingFormula = null,
} = {}) => {
  const showKpiMetricsTable = kpiScoreRows.length > 0;

  if (
    !rankingFormula &&
    !summaryRows.length &&
    !kpiScoreRows.length &&
    !kpiFactorCalculation?.steps?.length &&
    !priceCalculation?.formula &&
    priceCalculation?.price == null
  ) {
    return (
      <p className="text-sm text-gray-700">No KPI factor breakdown is available for this carrier.</p>
    );
  }

  return (
    <div className="space-y-5 text-sm text-[#030229]">
      {rankingFormula ? (
        <div>
          <p className="mb-1 text-dense-xs font-semibold uppercase tracking-wide text-gray-600">
            Carrier rank (RankCost)
          </p>
          <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-dense-xs text-gray-800">
            {rankingFormula}
          </p>
        </div>
      ) : null}
      {renderScoreDetailsTable(summaryRows, { title: "Score summary", valueLabel: "Value" })}
      {showKpiMetricsTable ? renderKpiMetricsTable(kpiScoreRows) : null}
      {renderPriceCalculationSection(priceCalculation)}
      {renderCalculationSection("KPI factor calculation", kpiFactorCalculation)}
      {!kpiScoreRows.length &&
      !kpiFactorCalculation?.steps?.length &&
      !priceCalculation?.formula ? (
        <p className="text-sm text-gray-700">
          Individual KPI scores are not available yet for this carrier.
        </p>
      ) : null}
    </div>
  );
};

const findRankedCarrierRow = (rankedCarriers, carrierCode) => {
  const code = (carrierCode || "").toString().trim().toUpperCase();
  if (!code) return null;
  return (rankedCarriers || []).find(
    (row) =>
      (row?.carrier_code || row?.carrierCode || "").toString().trim().toUpperCase() === code
  );
};

const renderScoringExplanationBody = (fullPayload, carrierCode, rankedCarrierRow) => {
  if (!fullPayload && !rankedCarrierRow) {
    return (
      <p className="text-sm text-gray-700">No scoring explanation is available for this carrier.</p>
    );
  }

  return renderScoreDetailsBreakdown(
    collectCarrierScoringBreakdown(fullPayload, carrierCode, rankedCarrierRow)
  );
};

// ---------- pure helper functions ----------

const titleCase = (value) =>
  String(value || "")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatRouteLocation = (location, fallback = "") => {
  const code = location?.locationCode || location?.code || location;
  if (!code || typeof code !== "string") return fallback;

  const parts = code.split("-").filter(Boolean);
  // Scan for a 2-letter country code followed by a 2-letter state code anywhere in the segments
  for (let i = 0; i < parts.length - 1; i++) {
    if (/^[A-Z]{2}$/i.test(parts[i]) && /^[A-Z]{2}$/i.test(parts[i + 1])) {
      const state = parts[i + 1].toUpperCase();
      // First non-numeric, non-ZIP segment after the state is the city
      const cityPart = parts.slice(i + 2).find((p) => !/^\d+$/.test(p));
      if (cityPart) return `${titleCase(cityPart)}, ${state}`;
      return state;
    }
  }
  // No country/state pair found — return fallback rather than a raw facility name
  return fallback || code;
};

// Returns the DB location_name when available, else "City, ST" from the location code
// stopNameByZip: { "31408": "Amazon Savannah", ... } — derived from shipment stops, checked first
const resolveStopLabel = (location, nameMap = {}, fallback = "", stopNameByZip = {}) => {
  const code = location?.locationCode || location?.code || location;
  if (code && typeof code === "string") {
    const zip = code.split("-").filter(Boolean).reverse().find((p) => /^\d{5}$/.test(p));
    if (zip && stopNameByZip[zip]) return stopNameByZip[zip];
  }
  const name = code && nameMap[code];
  if (name) return name;
  return formatRouteLocation(location, fallback);
};

const iconForTransportMode = (transportMode) => {
  const mode = String(transportMode || "").toUpperCase();
  if (mode.includes("SEA") || mode.includes("OCEAN")) return Ship;
  if (mode.includes("RAIL")) return TrainFront;
  return Truck;
};

const formatTransportMode = (transportMode, fallback = "Transport") => {
  const raw = String(transportMode || "").trim();
  if (!raw) return fallback;
  return titleCase(raw.replace(/_/g, " "));
};

function revenueWarningForSeg(seg, index) {
  const s = seg?.routeRate?.revenueStatus;
  if (!s || s === "APPLICABLE") return null;
  const mode = formatTransportMode(seg?.route?.transportMode, "leg");
  if (s === "NOT_PROVIDED") return `Leg ${index + 1} (${mode}): no revenue rate in contract`;
  if (s === "NOT_APPLICABLE") {
    return `Leg ${index + 1} (${mode}): ${seg.routeRate.revenueReason || "rate basis does not apply to this shipment"}`;
  }
  return `Leg ${index + 1} (${mode}): unsupported revenue basis`;
}

const rawRouteCode = (location) => {
  const code = location?.locationCode || location?.code || location;
  return typeof code === "string" ? code.trim().toUpperCase() : "";
};

// eslint-disable-next-line no-unused-vars -- kept for potential future use
const routeStopsFromSegments = (segments = [], originFallback, destinationFallback) => {
  const nodes = [];
  const pushNode = (location, fallback) => {
    const label = formatRouteLocation(location, fallback);
    if (!label) return;
    const code = rawRouteCode(location);
    const prev = nodes[nodes.length - 1];
    if (prev && code && prev.code === code) return;
    nodes.push({ label, code });
  };

  segments.forEach((segment, index) => {
    pushNode(segment?.route?.origin, index === 0 ? originFallback : "");
    pushNode(segment?.route?.destination, "");
  });

  if (!nodes.length) {
    pushNode(null, originFallback);
    pushNode(null, destinationFallback);
  }

  return nodes.map(({ label }) => ({ label }));
};

// ---------- component ----------

const IntermodalRatesShoppingModal = ({
  onClose,
  showModal,
  origin,
  originType,
  destination,
  destinationType,
  originAddress,
  destinationAddress,
  pickupDate,
  strategy,
  shipmentId,
  shipmentNumber,
  fetchShipmentDetails,
  stops,
  shipmentDetails,
  totalMiles,
  totalMinutes,
  officeCode,
  onAssignmentComplete,
}) => {
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [expandedCarrier, setExpandedCarrier] = useState(null);
  const [rateShopping, setRateShopping] = useState([]);
  const [rankedCarriers, setRankedCarriers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [initialAssignedCarrier, setInitialAssignedCarrier] = useState(null);
  const [rejectedCarriers, setRejectedCarriers] = useState([]);
  const [recalledCarriers, setRecalledCarriers] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState(shipmentDetails?.paymentMethod || "PREPAID");
  const [selectionOutcomeId, setSelectionOutcomeId] = useState(null);
  const [scoringExplanation, setScoringExplanation] = useState(null);
  const [scoringExplanationLoading, setScoringExplanationLoading] = useState(false);
  const [scoreDetailsCarrier, setScoreDetailsCarrier] = useState(null);
  const [missingQuantities, setMissingQuantities] = useState([]);
  const [brokerName, setBrokerName] = useState(officeCode || "");
  const [priceSort, setPriceSort] = useState("price-asc");
  const [locationNameMap, setLocationNameMap] = useState({});

  const stopNameByZip = React.useMemo(() => {
    const map = {};
    (stops || []).forEach((stop) => {
      const zip = (stop.zipCode || "").trim();
      const name = (stop.stopName || stop.warehouseName || "").trim();
      if (zip && name) map[zip] = name;
    });
    return map;
  }, [stops]);

  // ---------- utils ----------
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const kmToMiles = (km) => (km * 0.621371192).toFixed(1);
  const getId = (x) =>
    (x?.carrierCode || x?.carrierName || "").toString().toUpperCase();

  const buildRows = (items = []) =>
    (items || []).map((it) => ({
      type: it.type,
      basis: it.basis || "N/A",
      cost: it.cost ?? 0,
      revenue: it.revenue ?? 0,
      profit: it.profit ?? 0,
      currency: it.currency?.currencyCode || "USD",
      rateCarrierCode: it.rateCarrierCode || "—",
    }));

  const formatMoney = (value, currency = "USD") => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return `$${n.toFixed(2)} ${currency}`;
  };

  const formatMoneyCompact = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return `$${n.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatAllocationPct = (value) => {
    if (value == null || value === "") return "—";
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return `${Number.isInteger(n) ? n : n.toFixed(1)}%`;
  };

  // Prefer top-level arrays, fallback to route segment / breakdowns
  const extractCarrierCode = (result) => {
    const upper = (v) => (v ?? "").toString().toUpperCase();
    const isRealCarrierCode = (code) => Boolean(code) && code !== "VIRTUAL";
    const firstRealCode = (codes) =>
      (codes || []).map(upper).find(isRealCarrierCode) || "";

    const routeCarrierCode = firstRealCode(result?.routeCarrierCodes);
    if (routeCarrierCode) return routeCarrierCode;

    const rateCarrierCode = firstRealCode(result?.rateCarrierCodes);
    if (rateCarrierCode) return rateCarrierCode;

    const seg = (result?.pathSegments || []).find(
      (segment) =>
        upper(segment?.route?.transportMode) !== "VIRTUAL" &&
        isRealCarrierCode(upper(segment?.routeCarrierCode))
    );
    if (seg?.routeCarrierCode) return upper(seg.routeCarrierCode);

    const pickBreakCode = (arr) =>
      (arr || []).find((b) => isRealCarrierCode(upper(b?.rateCarrierCode)))?.rateCarrierCode;

    const rr = seg?.routeRate;
    const fromBreakdowns =
      pickBreakCode(rr?.rateBreakdown) ||
      pickBreakCode(rr?.accBreakdown) ||
      pickBreakCode(rr?.adjustedBreakdown);

    return upper(fromBreakdowns);
  };

  const injectFlatBaseIfMissing = (adjustedList = [], rateList = []) => {
    const normalized = (s) => (s ?? "").toString().toUpperCase();
    const hasBase = adjustedList.some((b) => normalized(b.type) === "BASE");
    if (hasBase) return [...adjustedList];

    const flatBase = rateList.find(
      (b) =>
        normalized(b.type) === "BASE" &&
        normalized(b.basis) === "FLAT"
    );
    return flatBase ? [...adjustedList, flatBase] : [...adjustedList];
  };

  // ---------- API fetchers ----------
  const fetchRateShopping = async () => {
    try {
      const response = await httpClient.post(
        `/api/organizations/${officeCode}/rate-shopping/all-paths`,
        {},
        {
          params: {
            origin,
            destination,
            originType: "GEOGRAPHY",
            destinationType: "GEOGRAPHY",
            operationDate: pickupDate,
            strategy: strategy || "ALL",
            totalMiles: totalMiles ?? undefined,
            totalMinutes: totalMinutes ?? undefined,
            originAddress,
            destinationAddress,
            paymentMethod,
            shipmentNumber: shipmentNumber ?? undefined,
          },
          headers: {
            "Content-Type": "application/json",
            "Office-Code": officeCode,
          },
        }
      );
      const parsed = parseRateShoppingResponse(response.data, {
        extractOutcomeId: extractSelectionOutcomeId,
      });
      if (response.data?.message && !parsed.paths.length && !parsed.rankedCarriers.length) {
        setRateShopping([]);
        setRankedCarriers([]);
        setSelectionOutcomeId(null);
        return;
      }
      setRankedCarriers(parsed.rankedCarriers);
      setRateShopping(parsed.paths);
      setSelectionOutcomeId(parsed.outcomeId);

      // Batch-fetch location names for all stop codes from the returned paths
      const codes = new Set();
      (parsed.paths || []).forEach((path) => {
        (path.pathSegments || []).forEach((seg) => {
          const oc = seg?.route?.origin?.locationCode;
          const dc = seg?.route?.destination?.locationCode;
          if (oc) codes.add(oc);
          if (dc) codes.add(dc);
        });
      });
      if (codes.size > 0) {
        httpClient
          .get("/api/locations/names-by-codes", {
            params: { codes: Array.from(codes) },
            headers: { "Office-Code": officeCode },
          })
          .then((r) => setLocationNameMap(r.data || {}))
          .catch(() => {});
      }
    } catch (error) {
      setRateShopping([]);
      setRankedCarriers([]);
      setSelectionOutcomeId(null);
      console.error("Rate shopping error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const scoringFetchKeyRef = useRef("");

  useEffect(() => {
    if (!showModal) {
      setScoreDetailsCarrier(null);
      scoringFetchKeyRef.current = "";
    }
  }, [showModal]);

  const resolveSelectionOutcomeId = useCallback(async () => {
    if (selectionOutcomeId) return selectionOutcomeId;
    if (!shipmentId) return null;

    const scope = { originatorId: officeCode, originatorCode: officeCode };

    try {
      const listResponse = await resourceSelectionApi.listShipmentSelectionOutcomes(
        shipmentId,
        scope
      );
      const fromList = pickLatestSelectionOutcomeId(listResponse.data);
      if (fromList) {
        setSelectionOutcomeId(fromList);
        return fromList;
      }
    } catch (error) {
      console.warn("Unable to list selection outcomes:", error);
    }

    try {
      const selectResponse = await resourceSelectionApi.selectShipmentById(
        shipmentId,
        { skip_rate_shopping: true, skip_shipment_tender: true },
        officeCode ? { "Office-Code": officeCode } : {}
      );
      const fromSelect = extractSelectionOutcomeId(selectResponse.data);
      if (fromSelect) {
        setSelectionOutcomeId(fromSelect);
        return fromSelect;
      }
    } catch (error) {
      console.warn("Unable to resolve selection outcome via select:", error);
    }

    return null;
  }, [shipmentId, selectionOutcomeId, officeCode]);

  const ensureRankedCarriersOnOutcome = useCallback(
    async (outcomeId) => {
      if (!shipmentId || !outcomeId || !rankedCarriers.length) return false;

      const scope = { originatorId: officeCode, originatorCode: officeCode };
      const carriers = rankedCarriers
        .map((row) => ({
          carrier_code: (row?.carrier_code || row?.carrierCode || "").toString().trim(),
          score: row?.score ?? null,
          kpi_factor: row?.kpi_factor ?? row?.kpiFactor ?? null,
          cost: row?.cost ?? null,
          price: row?.price ?? null,
        }))
        .filter((row) => row.carrier_code);

      if (!carriers.length) return false;

      try {
        await resourceSelectionApi.saveRankedCarrierSnapshot(
          shipmentId,
          outcomeId,
          { carriers, verify_capacity: false, verifyCapacity: false },
          scope
        );
        invalidateScoringExplanationCache(shipmentId, outcomeId);
        return true;
      } catch (error) {
        if (error?.response?.status === 409) {
          invalidateScoringExplanationCache(shipmentId, outcomeId);
          return true;
        }
        console.warn("Unable to persist ranked carriers for scoring explanation:", error);
        return false;
      }
    },
    [shipmentId, rankedCarriers, officeCode]
  );

  const loadScoringExplanation = useCallback(async (carrierCode, { force = false } = {}) => {
    if (!shipmentId) return null;

    const outcomeId = await resolveSelectionOutcomeId();
    if (!outcomeId) return null;

    await ensureRankedCarriersOnOutcome(outcomeId);

    const cacheScope = carrierCode ? `${outcomeId}:${carrierCode}` : outcomeId;
    const cached = getCachedScoringExplanation(shipmentId, cacheScope);
    if (!force && cached && isUsefulScoringExplanation(cached, carrierCode)) {
      setScoringExplanation(cached);
      return cached;
    }

    setScoringExplanationLoading(true);
    try {
      const response = await resourceSelectionApi.getScoringExplanation(
        shipmentId,
        outcomeId,
        {
          originatorId: officeCode,
          originatorCode: officeCode,
          carrierCode,
        }
      );
      setCachedScoringExplanation(shipmentId, cacheScope, response.data);
      setScoringExplanation(response.data);
      return response.data;
    } catch (error) {
      console.warn("Scoring explanation fetch failed:", error);
      return null;
    } finally {
      setScoringExplanationLoading(false);
    }
  }, [shipmentId, officeCode, resolveSelectionOutcomeId, ensureRankedCarriersOnOutcome]);

  const fetchRejectedCarriers = async () => {
    try {
      const res = await httpClient.get(
        `/api/carrier-selection/${officeCode}/${shipmentNumber}/actions/REJECT`,
        { headers: { "Office-Code": officeCode } }
      );
      setRejectedCarriers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (err?.response?.status === 404) {
        setRejectedCarriers([]);
      } else {
        console.error("Rejected fetch error:", err);
        setRejectedCarriers([]);
      }
    }
  };

  const fetchRecalledCarriers = async () => {
    try {
      const res = await httpClient.get(
        `/api/carrier-selection/${officeCode}/${shipmentNumber}/actions/RECALL`,
        { headers: { "Office-Code": officeCode } }
      );
      setRecalledCarriers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (err?.response?.status === 404) {
        setRecalledCarriers([]);
      } else {
        console.error("Recalled fetch error:", err);
        setRecalledCarriers([]);
      }
    }
  };

  const fetchBasisCheck = async () => {
    if (!shipmentNumber) return;
    try {
      const freshToken = Cookies.get("authToken");
      const res = await httpClient.post(
        `/api/organizations/${officeCode}/rate-shopping/check-basis`,
        {},
        {
          params: { shipmentNumber },
          headers: { Authorization: `Bearer ${freshToken}`, "Office-Code": officeCode },
        }
      );
      setMissingQuantities(res.data?.missingCostQuantities ?? []);
    } catch (err) {
      console.warn("Basis pre-check failed (non-blocking):", err);
      setMissingQuantities([]);
    }
  };

  // ---------- initial load ----------
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (
        origin &&
        destination &&
        pickupDate &&
        Cookies.get("authToken") &&
        Cookies.get("officeCode")
      ) {
        try {
          await Promise.all([
            fetchRateShopping(),
            fetchRejectedCarriers(),
            fetchRecalledCarriers(),
            fetchBasisCheck(),
          ]);
        } catch (error) {
          console.error("Fetch error:", error);
        } finally {
          if (isMounted) setIsLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [origin, destination, pickupDate, showModal, paymentMethod, shipmentNumber, officeCode]);

  // Resolve broker/org name from officeCode for multi-leg card headers.
  useEffect(() => {
    if (!showModal || !officeCode) return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await httpClient.get(
          `/api/organizations/search`,
          {
            params: { searchTerm: officeCode, page: 0, size: 20 },
            headers: {
              Accept: "application/json",
              "Office-Code": officeCode,
            },
          }
        );

        const rows = Array.isArray(data?.content)
          ? data.content
          : Array.isArray(data)
          ? data
          : [];
        const wanted = officeCode.toString().trim().toUpperCase();
        const match =
          rows.find(
            (row) =>
              (row?.officeCode || row?.OfficeCode || "")
                .toString()
                .trim()
                .toUpperCase() === wanted
          ) || rows[0];
        const resolvedName = (match?.name || "").toString().trim();

        if (!cancelled && resolvedName) {
          setBrokerName(resolvedName);
        }
      } catch (error) {
        console.warn("Unable to resolve broker/org name:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showModal, officeCode]);

  const carriersLoaded =
    !isLoading && (rateShopping.length > 0 || rankedCarriers.length > 0);

  useEffect(() => {
    if (!showModal || !carriersLoaded || !shipmentId) return;

    const fetchKey = `${shipmentId}:${selectionOutcomeId || "pending"}`;
    if (scoringFetchKeyRef.current === fetchKey && scoringExplanation) return;
    scoringFetchKeyRef.current = fetchKey;

    let cancelled = false;
    (async () => {
      const data = await loadScoringExplanation();
      if (!cancelled && !data) {
        console.warn("Scoring explanation not available for this shipment.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    showModal,
    carriersLoaded,
    shipmentId,
    selectionOutcomeId,
    loadScoringExplanation,
    scoringExplanation,
  ]);

  // ---------- shape + dedupe + sort results ----------
  const rankedCarrierMap = React.useMemo(
    () => buildRankedCarrierMap(rankedCarriers),
    [rankedCarriers]
  );

  // Child shipments are individual legs — only single-leg options should be offered.
  const isChildShipment = /-\d+$/.test(shipmentNumber || "");

  const rawResults = React.useMemo(() => {
    const pathRows = normalizeRateShoppingResults(rateShopping);
    const sourceRows =
      pathRows.length > 0
        ? pathRows
        : rankedCarriers
            .map((row, index) => {
              const code = (row?.carrier_code || row?.carrierCode || "")
                .toString()
                .trim()
                .toUpperCase();
              if (!code) return null;
              return {
                routeCarrierCodes: [code],
                rateCarrierCodes: [code],
                pathSegments: [],
                pathTotalCost: row?.cost ?? 0,
                adjustedPathTotalCost: row?.cost ?? 0,
                pathTotalPrice: row?.price ?? 0,
                adjustedPathTotalPrice: row?.price ?? 0,
                pathTotalTime: (Number(totalMinutes) || 0) / 60,
                __rank: row?.rank ?? index + 1,
              };
            })
            .filter(Boolean);

    return sourceRows
      .filter((result) => {
        const hasPhysicalSegments = (result?.pathSegments || []).some(
          (segment) => String(segment?.route?.transportMode || "").toUpperCase() !== "VIRTUAL"
        );
        const hasSegments = Array.isArray(result?.pathSegments) && result.pathSegments.length > 0;
        const hasCarrierIdentity = Boolean(extractCarrierCode(result));
        return hasCarrierIdentity && (!hasSegments || hasPhysicalSegments);
      })
      .map((result, index) => {
        const physicalSegments = (result.pathSegments || []).filter(
          (segment) => String(segment?.route?.transportMode || "").toUpperCase() !== "VIRTUAL"
        );
        const distanceInKm = physicalSegments.reduce(
          (total, segment) => total + (Number(segment?.route?.distanceInKm) || 0),
          0
        );
        const carrierCode = extractCarrierCode(result);
        const rateBreakdown = physicalSegments.flatMap(
          (s) => s.routeRate?.rateBreakdown || []
        );
        const accBreakdown = physicalSegments.flatMap(
          (s) => s.routeRate?.accBreakdown || []
        );
        const adjustedBreakdown = injectFlatBaseIfMissing(
          physicalSegments.flatMap((s) => s.routeRate?.adjustedBreakdown || []),
          rateBreakdown
        );
        const isCostUnresolvable = physicalSegments.some(
          (s) => s.routeRate?.costStatus === "COST_UNRESOLVABLE"
        );
        const routeMissingQuantities = isCostUnresolvable
          ? [...new Set(physicalSegments.flatMap(
              (s) => s.routeRate?.missingQuantities || []
            ))]
          : [];
        const rateCarrierName = physicalSegments
          .flatMap((segment) => segment?.routeRate?.rateBreakdown || [])
          .find((breakdown) => breakdown?.rateCarrierName)?.rateCarrierName;
        const routeCarrierName = physicalSegments.find(
          (segment) =>
            segment?.routeCarrierName &&
            String(segment?.routeCarrierCode || "").toUpperCase() !== "VIRTUAL"
        )?.routeCarrierName;

        const carrierName =
          routeCarrierName ||
          (Array.isArray(result?.routeCarrierNames) && result.routeCarrierNames.find(Boolean)) ||
          rateCarrierName ||
          (Array.isArray(result?.rateCarrierCodes) && result.rateCarrierCodes[0]) ||
          (Array.isArray(result?.routeCarrierCodes) && result.routeCarrierCodes[0]) ||
          carrierCode ||
          "N/A";

        const displayName =
          carrierName && carrierName !== carrierCode
            ? `${carrierName} (${carrierCode})`
            : carrierCode;

        const ranked = rankedCarrierMap.get(carrierCode);

        const breakdownTotalCost = adjustedBreakdown.reduce(
          (sum, b) => sum + Number(b.cost || 0),
          0
        );
        const breakdownTotalPrice = adjustedBreakdown.reduce(
          (sum, b) => sum + Number(b.price || 0),
          0
        );

        return {
          id: index,
          carrierName,
          carrierCode,
          displayName,
          rank: ranked?.rank ?? result.__rank ?? null,
          totalTime: Number(result.pathTotalTime || 0).toFixed(2),
          totalMiles: kmToMiles(distanceInKm),
          rateBreakdown,
          accBreakdown,
          adjustedBreakdown,
          totalCost:
            Number(result.adjustedPathTotalCost ?? 0) > 0
              ? Number(result.adjustedPathTotalCost)
              : Number(result.pathTotalCost ?? 0),
          totalPrice:
            breakdownTotalPrice ||
            ranked?.price ||
            Number(result.adjustedPathTotalPrice ?? result.pathTotalPrice ?? 0),
          totalRevenue:
            physicalSegments.reduce((sum, seg) => sum + Number(seg.routeRate?.revenue || 0), 0) ||
            breakdownTotalPrice ||
            ranked?.price ||
            Number(result.adjustedPathTotalPrice ?? result.pathTotalPrice ?? 0),
          actualAllocationPct: ranked?.actualAllocationPct ?? null,
          originalTotalCost: Number(result.pathTotalCost ?? 0),
          originalTotalPrice: Number(result.pathTotalPrice ?? 0),
          adjustedTotalCost: Number(result.adjustedPathTotalCost ?? 0),
          adjustedTotalPrice: Number(result.adjustedPathTotalPrice ?? 0),
          isCostUnresolvable,
          routeMissingQuantities,
          pathIndex: index,
          segmentCount: physicalSegments.length,
          pathSegments: physicalSegments,
          revenueNotApplicable: physicalSegments.some(
            (seg) =>
              seg?.routeRate?.revenueStatus === "NOT_APPLICABLE" ||
              seg?.routeRate?.revenueStatus === "UNSUPPORTED" ||
              seg?.routeRate?.revenueStatus === "NOT_PROVIDED"
          ),
        };
      })
      .filter((item) => !isChildShipment || item.segmentCount <= 1);
  }, [rateShopping, rankedCarriers, rankedCarrierMap, totalMinutes, isChildShipment]);

  const formattedResults = React.useMemo(() => {
    const results = [...rawResults];
    if (paymentMethod === "COLLECT") {
      results.sort(compareCarriersByName);
    } else if (priceSort === "price-asc") {
      results.sort((a, b) => {
        if (a.revenueNotApplicable !== b.revenueNotApplicable) return a.revenueNotApplicable ? 1 : -1;
        return a.totalRevenue - b.totalRevenue;
      });
    } else if (priceSort === "desc" || priceSort === "price-desc") {
      results.sort((a, b) => {
        if (a.revenueNotApplicable !== b.revenueNotApplicable) return a.revenueNotApplicable ? 1 : -1;
        return b.totalRevenue - a.totalRevenue;
      });
    } else if (priceSort === "kpi-asc") {
      results.sort((a, b) => (a.totalPrice ?? 0) - (b.totalPrice ?? 0));
    } else if (priceSort === "kpi-desc") {
      results.sort((a, b) => (b.totalPrice ?? 0) - (a.totalPrice ?? 0));
    }
    return results;
  }, [rawResults, paymentMethod, priceSort]);

  // ---------- preselect based on current assignment ----------
  useEffect(() => {
    if (!showModal || !shipmentDetails || !formattedResults.length) return;

    const prevCode = (
      shipmentDetails?.carrierOfficeCode?.partyOfficeCode ||
      shipmentDetails?.carrierCode ||
      ""
    )
      .toString()
      .toUpperCase();

    const prevName = (shipmentDetails?.carrierName || "").toString().toUpperCase();

    const match = formattedResults.find((r) => {
      const code = (r.carrierCode || "").toString().toUpperCase();
      const name = (r.carrierName || "").toString().toUpperCase();
      return (prevCode && code === prevCode) || (prevName && name === prevName);
    });

    if (match) {
      const picked = { code: match.carrierCode, name: match.carrierName };
      setInitialAssignedCarrier(picked);
      setSelectedCarrier(picked);
      if (shipmentDetails?.paymentMethod !== "COLLECT") {
        setExpandedCarrier(match.pathIndex);
      }
    } else {
      setInitialAssignedCarrier(null);
    }
  }, [showModal, shipmentDetails, formattedResults]);

  // ---------- selection + expand handlers ----------
  const handleSelectCarrier = (carrierName, carrierCode, pathIndex) => {
    setSelectedCarrier({ code: carrierCode, name: carrierName, pathIndex });
  };

  const toggleExpandCarrier = (pathIndex) => {
    setExpandedCarrier((prev) => (prev === pathIndex ? null : pathIndex));
  };

  const openScoreDetails = async (carrierItem) => {
    const carrierCode = carrierItem?.carrierCode;
    const rankedRow = findRankedCarrierRow(rankedCarriers, carrierCode);

    let explanation = scoringExplanation;
    let breakdown = collectCarrierScoringBreakdown(explanation, carrierCode, rankedRow);

    const hasEnhancedKpiData = (b) =>
      b.kpiScoreRows.length > 0 ||
      (b.kpiFactorCalculation?.steps?.length ?? 0) > 0 ||
      Boolean(b.priceCalculation?.formula);

    if (!hasEnhancedKpiData(breakdown) && !scoringExplanationLoading) {
      const carrierExplanation = await loadScoringExplanation(carrierCode, { force: true });
      if (carrierExplanation) {
        explanation = carrierExplanation;
        breakdown = collectCarrierScoringBreakdown(carrierExplanation, carrierCode, rankedRow);
      }
    }

    if (!breakdown.summaryRows.length && !hasEnhancedKpiData(breakdown)) {
      notify.info({
        title: scoringExplanationLoading ? "Loading score details" : "Score details unavailable",
        message: scoringExplanationLoading
          ? "Scoring explanation is still loading. Try again in a moment."
          : "Resource selection has not produced a selection outcome for this shipment yet.",
      });
      return;
    }
    setScoreDetailsCarrier({ ...carrierItem, __scoreBreakdown: breakdown });
  };

  // ---------- confirm handler ----------
  const handleConfirm = async (shipmentNumber) => {
    if (!selectedCarrier) return;

    try {
      const freshOfficeCode = Cookies.get("officeCode");

      const selectedRates = normalizeRateShoppingResults(rateShopping);
      const selectedRate =
        selectedRates[selectedCarrier.pathIndex] ||
        selectedRates.find((result) => {
          const arr = Array.isArray(result?.rateCarrierCodes)
            ? result.rateCarrierCodes
            : result?.rateCarrierCodes
            ? [result.rateCarrierCodes]
            : [];
          const carrier = arr[0];
          return (
            eq(carrier, selectedCarrier?.name) || eq(carrier, selectedCarrier?.code)
          );
        });

      if (!selectedRate) throw new Error("No rate found for that carrier");

      const isRealCarrier = (code) => {
        const c = String(code || "").trim().toUpperCase();
        return c !== "" && c !== "VIRTUAL";
      };

      const shipmentLegSegments = (selectedRate.pathSegments || []).filter((segment) => {
        const mode = String(segment?.route?.transportMode || "").toUpperCase();
        const originCode = String(segment?.route?.origin?.locationCode || "").trim().toUpperCase();
        const destCode = String(segment?.route?.destination?.locationCode || "").trim().toUpperCase();
        return (
          mode !== "VIRTUAL" &&
          isRealCarrier(segment?.routeCarrierCode) &&
          originCode !== "" &&
          destCode !== "" &&
          originCode !== destCode
        );
      });

      if (!shipmentLegSegments.length) {
        throw new Error("The selected route does not contain a carrier-backed transport leg.");
      }

      // Always route through the multi-leg endpoint so that old child legs are deleted
      // before the new assignment is written, even when the new plan is a single direct leg.
      // The backend single-leg downgrade path (physicalLegs.size() == 1) handles cleanup
      // and assigns carrier + charges directly to the parent without creating child rows.
      const isMultiLeg = shipmentLegSegments.length >= 1;

      if (isMultiLeg) {
        const legAndChargesList = await Promise.all(
          shipmentLegSegments.map(async (segment) => {
            // Prefer adjustedBreakdown (distance-adjusted costs); fall back to rateBreakdown when
            // no adjustment was applied (e.g. PER_MILE rates never populate adjustedBreakdown).
            const adjustedBd = segment.routeRate?.adjustedBreakdown || [];
            const rawSource = adjustedBd.length > 0
              ? adjustedBd
              : (segment.routeRate?.rateBreakdown || []);
            const breakdown = rawSource.filter((b) => {
              const t = (b?.type || "").toString().toUpperCase();
              return (
                (t === "BASE" || t === "FSC") &&
                !(t === "FSC" && Number(b?.cost) === 0)
              );
            });
            const codes = [...new Set(breakdown.map((b) => b.type).filter(Boolean))];
            const descriptions = await Promise.all(
              codes.map(async (code) => {
                const { data } = await httpClient.get(
                  `/api/code-mapping/code/${code}`,
                  { headers: { "Office-Code": freshOfficeCode } }
                );
                return [code, data?.[0]?.description || code];
              })
            );
            const names = Object.fromEntries(descriptions);
            const carrierCode =
              segment.routeCarrierCode ||
              segment.routeRate?.rateBreakdown?.[0]?.rateCarrierCode;
            return {
              originLocCode: segment.route?.origin?.locationCode,
              destinationLocCode: segment.route?.destination?.locationCode,
              carrierCode,
              carrierName:
                segment.routeCarrierName ||
                segment.routeRate?.rateBreakdown?.[0]?.rateCarrierName,
              agentCode: segment.routeAgentCode || null,
              agentName: segment.routeAgentName || null,
              transportMode: segment.route?.transportMode,
              shipmentCharges:
                paymentMethod === "COLLECT"
                  ? []
                  : [
                      ...breakdown.map((b) => ({
                        amount: b.cost,
                        currency: b.currency?.currencyCode,
                        type: "COST",
                        code: b.type,
                        chargeApprovalStatus: "PREAPPROVED",
                        chargeName: names[b.type],
                      })),
                      ...(segment.routeRate?.revenueStatus === "APPLICABLE"
                        ? [
                            {
                              amount: Number(segment.routeRate.revenue),
                              currency:
                                segment.routeRate?.currency?.currencyCode || "USD",
                              type: "REVENUE",
                              code: "BASE",
                              chargeApprovalStatus: "PREAPPROVED",
                              chargeName: "Base Revenue",
                            },
                          ]
                        : []),
                    ],
            };
          })
        );

        await httpClient.put(
          `/api/shipment-charges-update/${shipmentNumber}/update-shipments-and-charges-multi-leg`,
          { legAndChargesList },
          {
            headers: {
              "Content-Type": "application/json",
              "Office-Code": freshOfficeCode,
            },
          }
        );

        notify.success({
          title: "Multi-leg shipment created",
          message: `#${shipmentNumber} now has ${legAndChargesList.length} shipment legs.`,
        });
        await fetchShipmentDetails?.();
        onClose();
        onAssignmentComplete?.();
        return;
      }

      // Single-leg confirm
      const breakdowns = shipmentLegSegments
        .flatMap((seg) => {
          const adjBd = seg.routeRate?.adjustedBreakdown || [];
          const src = adjBd.length > 0 ? adjBd : (seg.routeRate?.rateBreakdown || []);
          return src.filter((b) => {
            const t = (b?.type || "").toString().toUpperCase();
            return (
              (t === "BASE" || t === "FSC") &&
              !(t === "FSC" && Number(b?.cost) === 0)
            );
          });
        })
        .map((b) => ({
          code: b.type,
          amount: b.cost,
          currency: b.currency?.currencyCode,
        }));

      const distinctCodes = [...new Set(breakdowns.map((b) => b.code))];

      const mappings = await Promise.all(
        distinctCodes.map((code) =>
          httpClient.get(`/api/code-mapping/code/${code}`, {
            headers: { "Office-Code": freshOfficeCode },
          })
        )
      );

      const codeNameMap = mappings.reduce((map, res, idx) => {
        const code = distinctCodes[idx];
        const list = res.data || [];
        map[code] = list.length > 0 ? list[0].description : code;
        return map;
      }, {});

      const allLegsApplicable = shipmentLegSegments.every(
        (seg) => seg.routeRate?.revenueStatus === "APPLICABLE"
      );
      const totalRevenueFromSegments = allLegsApplicable
        ? shipmentLegSegments.reduce((sum, seg) => sum + Number(seg.routeRate?.revenue ?? 0), 0)
        : 0;
      const revenueForShipment = totalRevenueFromSegments;

      const shipmentCharges =
        paymentMethod === "COLLECT"
          ? []
          : [
              ...breakdowns.map(({ code, amount, currency }) => ({
                shipment: { id: shipmentId },
                amount,
                currency,
                type: "COST",
                code,
                chargeApprovalStatus: "PREAPPROVED",
                chargeName: codeNameMap[code],
              })),
              ...(revenueForShipment > 0
                ? [
                    {
                      shipment: { id: shipmentId },
                      amount: revenueForShipment,
                      currency:
                        shipmentLegSegments[0]?.routeRate?.currency?.currencyCode ||
                        "USD",
                      type: "REVENUE",
                      code: "BASE",
                      chargeApprovalStatus: "PREAPPROVED",
                      chargeName: "Base Revenue",
                    },
                  ]
                : []),
            ];

      const isOcean = shipmentLegSegments.some(
        (seg) => seg.route.transportMode === "SEA"
      );
      const isTruck = shipmentLegSegments.some(
        (seg) => seg.route.transportMode === "TRUCK"
      );

      const totalChargeFromBreakdown =
        revenueForShipment > 0
          ? revenueForShipment.toFixed(2)
          : breakdowns.reduce((sum, b) => sum + Number(b.amount || 0), 0).toFixed(2);

      const updatedCarrier = {
        carrierName: selectedCarrier?.name,
        totalCharge: totalChargeFromBreakdown,
        carrierCode: selectedCarrier?.code || selectedCarrier?.name,
        carrierOfficeCode: selectedCarrier?.code || selectedCarrier?.name,
        isOcean,
        isTruck,
        status: paymentMethod === "COLLECT" ? "Accepted" : "Assigned",
      };

      const response = await httpClient.put(
        `/api/shipment-charges-update/shipmentNumber/${shipmentNumber}/FULL_RESET`,
        { shipment: updatedCarrier, shipmentCharges },
        {
          headers: {
            "Content-Type": "application/json",
            "Office-Code": officeCode,
          },
        }
      );

      if (response.status === 200) {
        notify.success({
          title: paymentMethod === "COLLECT" ? "Shipment Accepted" : "Shipment Assigned",
          message:
            paymentMethod === "COLLECT"
              ? `#${shipmentNumber} accepted`
              : `#${shipmentNumber} assigned to ${selectedCarrier?.name}`,
        });
        onClose();
        onAssignmentComplete?.();
      }
    } catch (error) {
      console.error("Update error:", error);
      notify.error({
        title: "Failed to Assign Carrier",
        message:
          error?.response?.data?.content ||
          error?.response?.data?.message ||
          "Something went wrong while assigning the carrier. Please try again.",
      });
    }
  };

  // ---------- derived UI state ----------
  const isSameAsInitiallyAssigned =
    initialAssignedCarrier &&
    selectedCarrier &&
    eq(initialAssignedCarrier.code, selectedCarrier.code);

  // Re-plan guard: once the first leg has departed (parent status becomes In Transit,
  // Delivered, or Completed) the backend will reject the re-plan with 409.
  // Disable the button here so the user sees a clear message before hitting the network.
  const parentStatus = (shipmentDetails?.status || "").toLowerCase();
  const isReplanBlocked =
    parentStatus === "in transit" ||
    parentStatus === "delivered" ||
    parentStatus === "completed";

  const isConfirmDisabled =
    isLoading ||
    !selectedCarrier ||
    (initialAssignedCarrier && isSameAsInitiallyAssigned) ||
    isReplanBlocked;

  const isInitialFor = (item) =>
    !!initialAssignedCarrier &&
    (eq(initialAssignedCarrier.code, item.carrierCode) ||
      eq(initialAssignedCarrier.name, item.carrierName));

  const isSelectedFor = (item) =>
    !!selectedCarrier && selectedCarrier.pathIndex === item.pathIndex;

  const historicalSet = React.useMemo(() => {
    const s = new Set();
    (rejectedCarriers || []).forEach((h) => s.add(getId(h)));
    (recalledCarriers || []).forEach((h) => s.add(getId(h)));
    return s;
  }, [rejectedCarriers, recalledCarriers]);

  const { availableResults, recalledResults } = React.useMemo(() => {
    const available = [];
    const recalled = [];
    (formattedResults || []).forEach((it) => {
      (historicalSet.has(getId(it)) ? recalled : available).push(it);
    });
    if (paymentMethod === "COLLECT") {
      available.sort(compareCarriersByName);
      recalled.sort(compareCarriersByName);
    }
    return { availableResults: available, recalledResults: recalled };
  }, [formattedResults, historicalSet, paymentMethod]);

  // ---------- rendered card ----------
  const renderCarrierCard = (carrierItem) => {
    const routeLegs = carrierItem.pathSegments || [];

    // For each segment, its origin location code maps to the carrier that *departs* from there.
    // This gives intermediate rail terminals / dray ramps a meaningful name when no DB
    // facility name exists in locationNameMap — e.g. "CSX Transportation" for zip 31419.
    // locationNameMap wins (spread last), so real DB names are never overridden.
    const segOriginCarrierNames = {};
    routeLegs.forEach((seg) => {
      const oc = seg?.route?.origin?.locationCode;
      const carrier = seg?.routeCarrierName || seg?.routeAgentName || seg?.routeCarrierCode || seg?.routeAgentCode;
      if (oc && carrier && !segOriginCarrierNames[oc]) {
        const cityState = formatRouteLocation(seg?.route?.origin, "");
        segOriginCarrierNames[oc] = cityState ? `${carrier} – ${cityState}` : carrier;
      }
    });
    const resolvedNameMap = { ...segOriginCarrierNames, ...locationNameMap };

    // routeRate.revenue is the authoritative sell price per segment (set by the rate engine).
    // totalPrice falls back to ranked.price (KPI allocation price) which can differ for multi-leg.
    // Always derive display revenue from the segment sum to stay consistent with grandTotal.
    const isNotApplicable = carrierItem.revenueNotApplicable;
    const segRevenueTotal = (carrierItem.pathSegments || []).reduce(
      (s, seg) => s + Number(seg.routeRate?.revenue || 0), 0
    );
    const displayRevenue = isNotApplicable
      ? null
      : segRevenueTotal > 0
        ? segRevenueTotal
        : null;

    return (
      <li
        key={carrierItem.id ?? getId(carrierItem)}
        className={`rounded-xl border bg-white p-fluid-3 shadow-sm transition ${
          isSelectedFor(carrierItem)
            ? "border-blue-600 ring-2 ring-blue-300"
            : "border-gray-200"
        }`}
      >
        {/* Header */}
        <div className="mb-fluid-3 flex items-center gap-fluid-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#223688] shadow-sm">
            <FaTruckFront className="text-white text-base" />
          </div>
          <h4 className="text-fluid-base font-semibold text-[#030229] flex items-center gap-fluid-1">
            {carrierItem.segmentCount > 1
              ? brokerName || carrierItem.displayName || carrierItem.carrierName
              : carrierItem.carrierName ||
                carrierItem.carrierCode ||
                carrierItem.displayName ||
                brokerName}
          </h4>
          {carrierItem.segmentCount > 1 ? (
            <span className="rounded-full bg-[#e8ecff] px-2.5 py-1 text-xs font-semibold text-[#223688]">
              {carrierItem.segmentCount}-leg route
            </span>
          ) : null}
        </div>

        {/* Two-Column Layout */}
        <div className="flex flex-col gap-fluid-3 xl:flex-row xl:items-stretch">
          {/* Left Column */}
          <div className="min-w-0 flex-1 xl:border-r xl:border-gray-200 xl:pr-fluid-3">
            {/* Route stop timeline — horizontal */}
            {(() => {
              // Build deduplicated stop list with mode icon between each pair
              const stops = [];
              routeLegs.forEach((leg, index) => {
                const oCode = leg?.route?.origin?.locationCode;
                const dCode = leg?.route?.destination?.locationCode;
                const oLabel = resolveStopLabel(leg?.route?.origin, resolvedNameMap, index === 0 ? origin || "Origin" : "", stopNameByZip);
                const dLabel = resolveStopLabel(leg?.route?.destination, resolvedNameMap, index === routeLegs.length - 1 ? destination || "Destination" : "", stopNameByZip);
                const ModeIcon = iconForTransportMode(leg?.route?.transportMode);
                if (index === 0) stops.push({ label: oLabel, code: oCode });
                if (!stops.length || stops[stops.length - 1].code !== oCode) {
                  stops.push({ label: oLabel, code: oCode });
                }
                stops.push({ _icon: ModeIcon, _mode: leg?.route?.transportMode });
                if (stops[stops.length - 1]?.code !== dCode) {
                  stops.push({ label: dLabel, code: dCode });
                }
              });
              if (!stops.length) {
                stops.push({ label: resolveStopLabel(null, resolvedNameMap, origin || "Origin", stopNameByZip), code: origin });
                stops.push({ _icon: Truck, _mode: "TL" });
                stops.push({ label: resolveStopLabel(null, resolvedNameMap, destination || "Destination", stopNameByZip), code: destination });
              }
              return (
                <div className="overflow-x-auto rounded-xl border border-[#e1e6fb] bg-[#f7f8ff] px-fluid-2 pb-1">
                  <div className="flex min-w-max items-center py-fluid-2">
                    {stops.map((s, i) =>
                      s._icon ? (
                        <div key={`icon-${i}`} className="mx-2 flex min-w-[4.5rem] flex-1 items-center gap-1">
                          <div className="h-0 flex-1 border-t-2 border-dotted border-[#3654a5]" />
                          <s._icon
                            className="h-5 w-5 shrink-0 text-[#3654a5]"
                            aria-label={formatTransportMode(s._mode, "Transport")}
                          />
                          <div className="h-0 flex-1 border-t-2 border-dotted border-[#3654a5]" />
                        </div>
                      ) : (
                        <div key={`stop-${i}`} className="flex items-center gap-1.5 whitespace-nowrap text-fluid-sm font-bold text-[#030229]">
                          <MapPin className="h-4 w-4 shrink-0 text-[#223688]" />
                          <span>{s.label}</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Stats tiles */}
            <div className="mt-fluid-2 grid grid-cols-2 gap-fluid-2 md:grid-cols-4">
              <div className="rounded-xl border border-[#e1e6fb] bg-[#fbfcff] px-fluid-2 py-fluid-2">
                <p className="text-dense-xs font-semibold uppercase tracking-wide text-gray-500">
                  Pick up date
                </p>
                <p className="mt-1 text-fluid-sm font-semibold text-[#030229]">
                  {formatDate(shipmentDetails?.pickupDate)}
                </p>
              </div>
              <div className="rounded-xl border border-[#e1e6fb] bg-[#fbfcff] px-fluid-2 py-fluid-2">
                <p className="text-dense-xs font-semibold uppercase tracking-wide text-gray-500">
                  Total time
                </p>
                <p className="mt-1 text-fluid-sm font-semibold text-[#030229]">
                  {carrierItem.totalTime} hrs
                </p>
              </div>
              <div className="rounded-xl border border-[#e1e6fb] bg-[#fbfcff] px-fluid-2 py-fluid-2">
                <p className="text-dense-xs font-semibold uppercase tracking-wide text-gray-500">
                  Total distance
                </p>
                <p className="mt-1 text-fluid-sm font-semibold text-[#030229]">
                  {Number(shipmentDetails?.totalMiles) > 0
                    ? `${Number(shipmentDetails.totalMiles).toFixed(1)} miles`
                    : carrierItem.totalMiles
                    ? `${carrierItem.totalMiles} miles`
                    : "N/A"}
                </p>
              </div>
              <div className="rounded-xl border border-[#e1e6fb] bg-[#fbfcff] px-fluid-2 py-fluid-2">
                <p className="text-dense-xs font-semibold uppercase tracking-wide text-gray-500">
                  Office code
                </p>
                <p className="mt-1 text-fluid-sm font-semibold text-[#030229]">
                  {officeCode || "—"}
                </p>
              </div>
            </div>

            {/* Revenue / Profit tiles — PREPAID only */}
            {shipmentDetails?.paymentMethod !== "COLLECT" &&
              (() => {
                const tileRevenue = displayRevenue;
                const tileProfit = isNotApplicable
                  ? null
                  : (displayRevenue && displayRevenue > 0) ? displayRevenue - carrierItem.totalCost : 0;
                return (
                  <div className="mt-fluid-2 grid grid-cols-2 gap-fluid-2">
                    <div className="rounded-xl border border-[#e1e6fb] bg-[#fbfcff] px-fluid-2 py-fluid-2">
                      <p className="text-dense-xs font-semibold uppercase tracking-wide text-gray-500">
                        Revenue
                      </p>
                      <p className="mt-1 text-fluid-sm font-semibold text-[#030229]">
                        {isNotApplicable ? "—" : `${formatMoneyCompact(tileRevenue)} USD`}
                      </p>
                      {isNotApplicable &&
                        carrierItem.pathSegments
                          .map((seg, i) => revenueWarningForSeg(seg, i))
                          .filter(Boolean)
                          .map((msg, i) => (
                            <p key={i} className="mt-1 text-dense-xs font-semibold text-amber-600">{msg}</p>
                          ))
                      }
                    </div>
                    <div
                      className={`rounded-xl border px-fluid-2 py-fluid-2 ${
                        isNotApplicable
                          ? "border-gray-200 bg-gray-50"
                          : tileProfit >= 0
                          ? "border-green-200 bg-green-50"
                          : "border-red-200 bg-red-50"
                      }`}
                    >
                      <p className="text-dense-xs font-semibold uppercase tracking-wide text-gray-500">
                        Profit
                      </p>
                      <p
                        className={`mt-1 text-fluid-sm font-semibold ${
                          isNotApplicable ? "text-gray-400" : tileProfit >= 0 ? "text-green-700" : "text-red-600"
                        }`}
                      >
                        {isNotApplicable ? "—" : `${formatMoneyCompact(tileProfit)} USD`}
                      </p>
                    </div>
                  </div>
                );
              })()}

            {/* Expandable details */}
            <div
              className={`overflow-hidden transition-all duration-500 ease-in-out ${
                expandedCarrier === carrierItem.pathIndex
                  ? "max-h-[9999px] opacity-100"
                  : "max-h-0 opacity-0"
              }`}
            >
              <div className="mt-fluid-4 space-y-fluid-4 border-t border-dashed border-[#cfd7f4] pt-fluid-3 text-fluid-sm text-[#030229]">
                {/* Multi-leg route breakdown */}
                {carrierItem.segmentCount > 1 ? (
                  <section
                    aria-label="Route breakdown"
                    className="rounded-xl border border-[#dfe5fb] bg-[#f7f8ff] p-fluid-2"
                  >
                    <div className="mb-fluid-2 flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-fluid-base">Route breakdown</p>
                        <p className="text-xs text-gray-500">
                          Each chargeable movement in this route
                        </p>
                      </div>
                      <span className="rounded-full bg-[#223688] px-2.5 py-1 text-xs font-semibold text-white">
                        {carrierItem.segmentCount} legs
                      </span>
                    </div>
                    {(() => {
                      const legChargeRows = carrierItem.pathSegments.map((segment) => {
                        const rate = segment?.routeRate || {};
                        return injectFlatBaseIfMissing(
                          rate.adjustedBreakdown || [],
                          rate.rateBreakdown || []
                        ).filter((b) => {
                          const t = (b?.type || "").toString().toUpperCase();
                          if (t !== "BASE" && t !== "FSC") return false;
                          if (t === "FSC" && Number(b?.cost) === 0) return false;
                          return true;
                        });
                      });
                      // Use displayRevenue (pre-computed from segment routeRate.revenue) so
                      // grandTotal and the Revenue tile always show the same figure.
                      const grandTotal = displayRevenue > 0
                        ? displayRevenue
                        : legChargeRows.flat().reduce((s, b) => s + Number(b.cost || 0), 0);

                      return (
                        <div className="space-y-fluid-2">
                          {carrierItem.pathSegments.map((segment, index) => {
                            const route = segment?.route || {};
                            const rate = segment?.routeRate || {};
                            const mode = String(
                              route.transportMode || "Transport"
                            ).replace(/_/g, " ");
                            const LegIcon = iconForTransportMode(route.transportMode);
                            const segRouteCarrierName = segment?.routeCarrierName;
                            const segRouteCarrierCode = segment?.routeCarrierCode;
                            const resolvedRouteName =
                              segRouteCarrierName &&
                              segRouteCarrierName !== segRouteCarrierCode
                                ? segRouteCarrierName
                                : null;
                            const legAdjustedBreakdown = rate.adjustedBreakdown || [];
                            const legRateBreakdown = rate.rateBreakdown || [];
                            const carrier =
                              resolvedRouteName ||
                              segment?.routeAgentName ||
                              legAdjustedBreakdown.find((b) => b?.rateCarrierName)
                                ?.rateCarrierName ||
                              legRateBreakdown.find((b) => b?.rateCarrierName)
                                ?.rateCarrierName ||
                              segment?.routeAgentCode ||
                              segRouteCarrierCode ||
                              carrierItem.carrierCode ||
                              "—";
                            const legCharges = legChargeRows[index];
                            const legAccCharges = (rate.accBreakdown || []).filter(
                              (b) => Number(b?.cost) !== 0
                            );
                            return (
                              <div
                                key={`${route.routeId || "leg"}-${index}`}
                                className="overflow-hidden rounded-lg border border-[#d8e0f8] bg-white shadow-sm"
                              >
                                <div className="flex flex-wrap items-center gap-2 bg-[#223688] px-fluid-2 py-2 text-white">
                                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/20 text-xs font-bold">
                                    {index + 1}
                                  </span>
                                  <LegIcon className="h-4 w-4" />
                                  <span className="font-semibold">{carrier}</span>
                                  <span className="text-white/50">|</span>
                                  <span className="font-semibold">
                                    {resolveStopLabel(route.origin, resolvedNameMap, origin || "Origin", stopNameByZip)}
                                    {" → "}
                                    {resolveStopLabel(route.destination, resolvedNameMap, destination || "Destination", stopNameByZip)}
                                  </span>
                                  {(rate.revenueStatus === "NOT_APPLICABLE" || rate.revenueStatus === "UNSUPPORTED" || rate.revenueStatus === "NOT_PROVIDED") && (
                                    <span className="ml-auto rounded-full bg-amber-400 px-2 py-0.5 text-xs font-semibold text-amber-900">
                                      {rate.revenueStatus}{rate.revenueReason ? ` — ${rate.revenueReason}` : ""}
                                    </span>
                                  )}
                                </div>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-3 px-fluid-2 py-fluid-2 text-fluid-sm md:grid-cols-3">
                                  <div>
                                    <p className="text-dense-xs font-semibold uppercase text-gray-500">
                                      Mode
                                    </p>
                                    <p className="mt-1 font-semibold">{mode}</p>
                                  </div>
                                  <div>
                                    <p className="text-dense-xs font-semibold uppercase text-gray-500">
                                      Distance
                                    </p>
                                    <p className="mt-1 font-semibold">
                                      {kmToMiles(Number(route.distanceInKm || 0))} mi
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-dense-xs font-semibold uppercase text-gray-500">
                                      Time
                                    </p>
                                    <p className="mt-1 font-semibold">
                                      {Number(route.baseTransitTime || 0).toFixed(2)} h
                                    </p>
                                  </div>
                                </div>
                                <div className="border-t border-[#edf0fa]">
                                  <table className="min-w-full text-left text-fluid-sm">
                                    <thead className="bg-[#f7f8ff] text-dense-xs font-semibold uppercase tracking-wide text-gray-500">
                                      <tr>
                                        <th className="px-3 py-1.5">Code</th>
                                        <th className="px-3 py-1.5">Basis</th>
                                        <th className="px-3 py-1.5 text-right">
                                          Price (USD)
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {legCharges.length > 0 ? (
                                        legCharges.map((b, idx) => (
                                          <tr
                                            key={`${b.type}-${idx}`}
                                            className="border-t border-gray-100"
                                          >
                                            <td className="px-3 py-1.5 font-medium">
                                              {b.type || "—"}
                                            </td>
                                            <td className="px-3 py-1.5 text-gray-500">
                                              {b.basis
                                                ? typeof b.basis === "object"
                                                  ? b.basis.name || String(b.basis)
                                                  : String(b.basis)
                                                : "—"}
                                            </td>
                                            <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-[#223688]">
                                              {formatMoney(b.cost)}
                                            </td>
                                          </tr>
                                        ))
                                      ) : (
                                        <tr>
                                          <td
                                            colSpan={3}
                                            className="px-3 py-2 text-center text-gray-400 italic text-xs"
                                          >
                                            No charges
                                          </td>
                                        </tr>
                                      )}
                                      {legAccCharges.length > 0 && (
                                        <>
                                          <tr className="border-t border-dashed border-gray-300">
                                            <td
                                              colSpan={3}
                                              className="px-3 py-1 text-dense-xs text-gray-400 italic"
                                            >
                                              Accessorial charges — informational only
                                            </td>
                                          </tr>
                                          {legAccCharges.map((b, idx) => (
                                            <tr
                                              key={`acc-${b.type}-${idx}`}
                                              className="border-t border-gray-100 text-gray-500"
                                            >
                                              <td className="px-3 py-1.5">
                                                {b.type || "—"}
                                              </td>
                                              <td className="px-3 py-1.5">
                                                {b.basis
                                                  ? typeof b.basis === "object"
                                                    ? b.basis.name || String(b.basis)
                                                    : String(b.basis)
                                                  : "—"}
                                              </td>
                                              <td className="px-3 py-1.5 text-right tabular-nums">
                                                {formatMoney(b.cost)}
                                              </td>
                                            </tr>
                                          ))}
                                        </>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                                <div className="flex items-center justify-between border-t border-[#edf0fa] px-3 py-1.5 text-fluid-sm">
                                  <span className="text-gray-500 font-semibold text-dense-xs uppercase">Revenue</span>
                                  <span className={`tabular-nums font-semibold ${rate.revenueStatus === "APPLICABLE" ? "text-[#223688]" : "text-gray-400"}`}>
                                    {rate.revenueStatus === "APPLICABLE" ? formatMoney(rate.revenue) : "—"}
                                  </span>
                                </div>
                              </div>
                            );
                          })}

                          {/* Grand total */}
                          <div className="flex items-center justify-between rounded-lg border border-[#d8e0f8] bg-[#f0f3ff] px-4 py-3">
                            <span className="font-semibold text-fluid-sm text-[#223688]">
                              Total Charge
                            </span>
                            <span className="font-bold text-fluid-sm tabular-nums text-[#223688]">
                              {formatMoney(grandTotal)}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </section>
                ) : null}

                {/* Single-leg charge table */}
                {carrierItem.segmentCount <= 1 &&
                  (() => {
                    const singleLegCharges = carrierItem.adjustedBreakdown.filter((b) => {
                      const t = (b.type || "").toString().toUpperCase();
                      if (t !== "BASE" && t !== "FSC") return false;
                      if (t === "FSC" && Number(b?.cost) === 0) return false;
                      return true;
                    });
                    const singleCostTotal = singleLegCharges.reduce(
                      (s, b) => s + Number(b.cost || 0),
                      0
                    );
                    const singleTotal = displayRevenue > 0 ? displayRevenue : singleCostTotal;
                    const singleAccCharges = (carrierItem.accBreakdown || []).filter(
                      (b) => Number(b?.cost) !== 0
                    );
                    return (
                      <div>
                        <p className="font-semibold mb-fluid-1">Charge Breakdown</p>
                        <div className="overflow-x-auto rounded-lg border border-gray-200">
                          <table className="min-w-full text-left text-fluid-sm">
                            <thead className="bg-gray-50 text-dense-xs font-semibold uppercase tracking-wide text-gray-700">
                              <tr>
                                <th className="px-3 py-2">Code</th>
                                <th className="px-3 py-2">Basis</th>
                                <th className="px-3 py-2 text-right">Price (USD)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {singleLegCharges.length > 0 ? (
                                singleLegCharges.map((b, idx) => (
                                  <tr
                                    key={`${b.type}-${idx}`}
                                    className="border-t border-gray-100"
                                  >
                                    <td className="px-3 py-2 font-medium">
                                      {b.type || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600">
                                      {b.basis
                                        ? typeof b.basis === "object"
                                          ? b.basis.name || String(b.basis)
                                          : String(b.basis)
                                        : "—"}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">
                                      {formatMoney(b.cost)}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td
                                    colSpan={3}
                                    className="px-3 py-4 text-center text-gray-400 italic"
                                  >
                                    No rate charges available
                                  </td>
                                </tr>
                              )}
                              {singleLegCharges.length > 0 && (
                                <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
                                  <td className="px-3 py-2" colSpan={2}>
                                    Total
                                  </td>
                                  <td className="px-3 py-2 text-right tabular-nums">
                                    {formatMoney(singleTotal)}
                                  </td>
                                </tr>
                              )}
                              {singleAccCharges.length > 0 && (
                                <>
                                  <tr className="border-t border-dashed border-gray-300">
                                    <td
                                      colSpan={3}
                                      className="px-3 py-1 text-dense-xs text-gray-400 italic"
                                    >
                                      Accessorial charges — informational only
                                    </td>
                                  </tr>
                                  {singleAccCharges.map((b, idx) => (
                                    <tr
                                      key={`acc-${b.type}-${idx}`}
                                      className="border-t border-gray-100 text-gray-500"
                                    >
                                      <td className="px-3 py-2">{b.type || "—"}</td>
                                      <td className="px-3 py-2">
                                        {b.basis
                                          ? typeof b.basis === "object"
                                            ? b.basis.name || String(b.basis)
                                            : String(b.basis)
                                          : "—"}
                                      </td>
                                      <td className="px-3 py-2 text-right tabular-nums">
                                        {formatMoney(b.cost)}
                                      </td>
                                    </tr>
                                  ))}
                                </>
                              )}
                            </tbody>
                          </table>
                        </div>
                        {(() => {
                          const firstSegRate = carrierItem.pathSegments[0]?.routeRate || {};
                          return (
                            <div className="mt-2 flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 text-fluid-sm">
                              <span className="text-gray-500 font-semibold">Revenue</span>
                              <span className={`tabular-nums font-semibold ${firstSegRate.revenueStatus === "APPLICABLE" ? "text-[#223688]" : "text-gray-400"}`}>
                                {firstSegRate.revenueStatus === "APPLICABLE" ? formatMoney(firstSegRate.revenue) : "—"}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}
              </div>
            </div>
          </div>

          {/* Right Column: Summary + Actions */}
          <div className="mt-1 flex w-full flex-col items-center justify-center self-start rounded-xl border border-[#dfe5fb] bg-[#f7f8ff] p-5 xl:mt-0 xl:w-[22rem] xl:items-stretch">
            {shipmentDetails?.paymentMethod !== "COLLECT" && (
              <div className="mb-5 w-full">
                <div className="grid grid-cols-3 gap-2">
                  <div className="flex flex-col items-center rounded-lg border border-[#e1e6fb] bg-white px-2 py-2 text-center">
                    <p className="text-dense-xs font-semibold uppercase tracking-tight text-gray-500 whitespace-nowrap">
                      % Assigned
                    </p>
                    <p className="mt-1 tabular-nums font-bold text-[#223688] text-sm">
                      {formatAllocationPct(carrierItem.actualAllocationPct)}
                    </p>
                  </div>
                  <div className="flex flex-col items-center rounded-lg border border-[#e1e6fb] bg-white px-2 py-2 text-center">
                    <p className="text-dense-xs font-semibold uppercase tracking-tight text-gray-500">
                      Price
                    </p>
                    {carrierItem.isCostUnresolvable ? (
                      <span className="mt-1 inline-block rounded-full bg-amber-100 border border-amber-300 px-2 py-0.5 text-xs font-semibold text-amber-800 whitespace-nowrap">
                        Needs {(carrierItem.routeMissingQuantities?.[0]) || "quantity"}
                      </span>
                    ) : (
                      <p className="mt-1 tabular-nums font-bold text-[#223688] text-sm">
                        {isNotApplicable ? "—" : formatMoney(displayRevenue)}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-center rounded-lg border border-[#e1e6fb] bg-white px-2 py-2 text-center">
                    <p className="text-dense-xs font-semibold uppercase tracking-tight text-gray-500">
                      KPI-Cost
                    </p>
                    <p className="mt-1 tabular-nums font-bold text-[#223688] text-sm">
                      {formatMoney(carrierItem.totalPrice)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <button
              type="button"
              disabled={isInitialFor(carrierItem) || carrierItem.isCostUnresolvable}
              onClick={(e) => {
                e.stopPropagation();
                if (!isInitialFor(carrierItem) && !carrierItem.isCostUnresolvable) {
                  handleSelectCarrier(
                    carrierItem.carrierName,
                    carrierItem.carrierCode,
                    carrierItem.pathIndex
                  );
                }
              }}
              className={`w-full rounded-lg px-4 py-3 text-fluid-base font-semibold shadow-sm transition ${
                isInitialFor(carrierItem)
                  ? "bg-[#223688] text-white cursor-not-allowed"
                  : carrierItem.isCostUnresolvable
                    ? "bg-amber-200 text-amber-800 cursor-not-allowed"
                    : "bg-[#223688] text-white hover:bg-[#1a2d66]"
              }`}
            >
              {isInitialFor(carrierItem)
                ? "Assigned"
                : carrierItem.isCostUnresolvable
                ? "Unavailable"
                : carrierItem.segmentCount > 1
                ? `Select ${carrierItem.segmentCount}-leg route`
                : "Select"}
            </button>

            {shipmentDetails?.paymentMethod !== "COLLECT" && (
              <div className="mt-4 flex w-full items-center justify-center gap-6 text-fluid-sm">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpandCarrier(carrierItem.pathIndex);
                  }}
                  className="text-[#223688] underline"
                >
                  {expandedCarrier === carrierItem.pathIndex
                    ? "Hide details"
                    : "View details"}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openScoreDetails(carrierItem);
                  }}
                  disabled={scoringExplanationLoading && !scoringExplanation}
                  className="text-[#223688] underline disabled:cursor-wait disabled:opacity-60"
                >
                  Score details
                </button>
              </div>
            )}
          </div>
        </div>
      </li>
    );
  };

  // ---------- loading ----------
  if (isLoading) {
    return (
      <FullScreenSlideUpModal isOpen={showModal} onClose={onClose}>
        <div className="w-full text-center p-fluid-6">
          <p className="text-fluid-lg font-semibold text-blue-600 mb-fluid-4">
            Loading Carriers
          </p>
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </FullScreenSlideUpModal>
    );
  }

  const scoreDetailsOverlay =
    scoreDetailsCarrier &&
    ReactDOM.createPortal(
      <div
        className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/45 px-4"
        onClick={() => setScoreDetailsCarrier(null)}
      >
        <div
          className="w-full max-w-2xl rounded-2xl bg-white shadow-xl"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="score-details-title"
        >
          <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
            <div>
              <h3 id="score-details-title" className="text-lg font-semibold text-[#030229]">
                Score details
              </h3>
              <p className="mt-1 text-sm text-gray-700">
                {scoreDetailsCarrier.displayName || scoreDetailsCarrier.carrierName}
                {scoreDetailsCarrier.carrierCode
                  ? ` (${scoreDetailsCarrier.carrierCode})`
                  : ""}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setScoreDetailsCarrier(null)}
              className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              aria-label="Close score details"
            >
              <X size={20} />
            </button>
          </div>
          <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
            {scoreDetailsCarrier?.__scoreBreakdown
              ? renderScoreDetailsBreakdown(scoreDetailsCarrier.__scoreBreakdown)
              : renderScoringExplanationBody(
                  scoringExplanation,
                  scoreDetailsCarrier?.carrierCode,
                  findRankedCarrierRow(rankedCarriers, scoreDetailsCarrier?.carrierCode)
                )}
          </div>
        </div>
      </div>,
      document.body
    );

  // ---------- render ----------
  return (
    <>
      <FullScreenSlideUpModal isOpen={showModal} onClose={onClose}>
        {/* Shipment Overview */}
        <div className="sticky top-0 z-50 bg-custom-bg-light-purple pb-fluid-2 -mx-fluid-3 px-fluid-2">
          <div className="grid grid-cols-1 sm:grid-cols-4 lg:grid-cols-4 gap-fluid-3">
            <div className="bg-white rounded-2xl shadow px-fluid-3 py-fluid-2">
              <p className="text-dense-xs text-[#030229] font-semibold mb-1">Buyer</p>
              <p className="text-fluid-sm text-[#030229]">
                {shipmentDetails?.receiverName || "N/A"}
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow px-fluid-3 py-fluid-2">
              <p className="text-dense-xs text-[#030229] font-semibold mb-1">Created Date</p>
              <p className="text-fluid-sm text-[#030229]">
                {shipmentDetails?.createdDate
                  ? new Date(shipmentDetails.createdDate).toLocaleDateString()
                  : "N/A"}
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow px-fluid-3 py-fluid-2">
              <p className="text-dense-xs text-[#030229] font-semibold mb-1">
                Shipment Number
              </p>
              <p className="text-fluid-sm text-[#030229]">
                {shipmentDetails?.shipmentNumber || "N/A"}
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow px-fluid-3 py-fluid-2">
              <p className="text-dense-xs text-[#030229] font-semibold mb-1">Order Value</p>
              <p className="text-fluid-sm text-[#030229]">
                ${shipmentDetails?.orderValue || 0}
              </p>
            </div>
          </div>

          {/* Route Details */}
          <div className="grid grid-cols-1 sm:grid-cols-5 lg:grid-cols-5 gap-fluid-3 mt-fluid-2 items-end mb-fluid-2">
            <div>
              <p className="text-dense-xs text-[#030229] font-semibold mb-1">Origin</p>
              <input
                value={origin || ""}
                readOnly
                className="bg-white border px-3 py-2 rounded w-full text-fluid-sm text-[#030229]"
              />
            </div>
            <div>
              <p className="text-dense-xs text-[#030229] font-semibold mb-1">Destination</p>
              <input
                value={destination || ""}
                readOnly
                className="bg-white border px-3 py-2 rounded w-full text-fluid-sm text-[#030229]"
              />
            </div>
            <div>
              <p className="text-dense-xs text-[#030229] font-semibold mb-1">Pick up date</p>
              <input
                value={formatDate(shipmentDetails?.pickupDate)}
                readOnly
                className="bg-white border px-3 py-2 rounded w-full text-fluid-sm text-[#030229]"
              />
            </div>
            <div>
              <p className="text-dense-xs text-[#030229] font-semibold mb-1">Delivery date</p>
              <input
                value={formatDate(shipmentDetails?.deliveryDate)}
                readOnly
                className="bg-white border px-3 py-2 rounded w-full text-fluid-sm text-[#030229]"
              />
            </div>
            <div>
              <p className="text-dense-xs text-[#030229] font-semibold mb-1">Mode</p>
              <input
                value={shipmentDetails?.movementType || ""}
                readOnly
                className="bg-white border px-3 py-2 rounded w-full text-fluid-sm text-[#030229]"
              />
            </div>
          </div>
        </div>

        {/* Available Carriers */}
        <section aria-labelledby="available-carriers-heading">
          <div className="flex items-center justify-between">
            <h3
              id="available-carriers-heading"
              className="text-fluid-lg font-semibold text-[#030229]"
            >
              Available carriers
            </h3>
            {paymentMethod !== "COLLECT" && (
              <select
                value={priceSort}
                onChange={(e) => setPriceSort(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-fluid-sm text-[#030229] bg-white focus:outline-none focus:ring-2 focus:ring-[#223688]"
              >
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="kpi-asc">KPI-Cost: Low to High</option>
                <option value="kpi-desc">KPI-Cost: High to Low</option>
              </select>
            )}
          </div>
          {missingQuantities.length > 0 && (
            <div className="mt-fluid-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-fluid-3 py-fluid-2 text-fluid-sm text-amber-800">
              <span className="mt-0.5 shrink-0">⚠</span>
              <span>
                Some routes cannot be priced because this shipment is missing{" "}
                {missingQuantities.map((q) => q.label).join(", ")}.{" "}
                Add the missing quantities to the shipment items to enable all routes.
              </span>
            </div>
          )}

          <div className="my-fluid-2">
            {availableResults.length > 0 ? (
              <ul className="space-y-fluid-3">
                {availableResults.map((carrierItem) => renderCarrierCard(carrierItem))}
              </ul>
            ) : (
              <p className="text-gray-500 text-center py-fluid-1">
                No eligible carriers. Please select from the options in the Rejected/Recall
                section below.
              </p>
            )}
          </div>
        </section>

        {/* Previously rejected/recalled Carriers */}
        <section className="my-fluid-1" aria-labelledby="historical-carriers-heading">
          <div className="flex items-center justify-between mb-1">
            <h3
              id="historical-carriers-heading"
              className="text-fluid-lg font-semibold text-[#030229]"
            >
              Previously rejected/recalled carriers
            </h3>
          </div>
          <div className="my-fluid-1">
            {recalledResults.length > 0 ? (
              <ul className="space-y-fluid-3">
                {recalledResults.map((carrierItem) => renderCarrierCard(carrierItem))}
              </ul>
            ) : (
              <p className="text-gray-500 text-center py-fluid-4">
                No rejected or recalled carriers
              </p>
            )}
          </div>
        </section>

        {/* Footer */}
        <div className="sticky bottom-0 z-50 bg-custom-bg-light-purple border-t border-gray-200 flex justify-end gap-fluid-1 -px-fluid-3 sm:px-fluid-3 py-fluid-2 pr-fluid-4">
          <div className="flex gap-fluid-1 translate-x-[0.5rem] sm:translate-x-[1rem]">
            <button
              onClick={onClose}
              className="px-fluid-button-x py-fluid-button-y border border-gray-300 rounded text-gray-700 hover:bg-gray-100 text-fluid-sm transition"
            >
              Cancel
            </button>
            <div
              title={
                isReplanBlocked
                  ? "Re-planning is locked: the first leg has already started. Re-planning is only allowed while all legs are in Planned status."
                  : undefined
              }
            >
              <button
                onClick={() => handleConfirm(shipmentNumber)}
                disabled={isConfirmDisabled}
                className={`px-fluid-button-x py-fluid-button-y rounded text-white text-fluid-sm font-semibold transition ${
                  !isConfirmDisabled
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-blue-300 cursor-not-allowed"
                }`}
              >
                {isLoading ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      </FullScreenSlideUpModal>
      {scoreDetailsOverlay}
    </>
  );
};

export default IntermodalRatesShoppingModal;
